import { AuthenticityScoreAlert } from "views/applications-id-report/slices/CombinedIncomeReportSlice/components/AuthenticityScoreAlert";
import { resolveAuthenticityTone } from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/authenticityTone";
import {
  resolveAuthenticityScore,
  resolveFraudRisk,
  resolveRiskSummary
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/fraudRisk";
import {
  NAME_MISMATCH_LABEL,
  resolveDocumentProblems,
  toFraudRiskLines
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/utils/documentProblems";
import { DocumentIssueAlert } from "views/applications-id-report/slices/CombinedIncomeReportSlice/components/DocumentIssueAlert";
import { BankAccountDetailsSlice } from "./BankAccountDetailsSlice";
import { NameMismatchAlert } from "../../NameMismatchAlert";
import { Show } from "shared/components";
import { getBankStatementReasonCodes } from "../utils";
import { IncomeSummarySlice } from "./IncomeSummarySlice";
import { SimpleTable } from "../../PaystubTable/components/SimpleTable";
import { useMemo, useState } from "react";
import { cx, formatToCurrency } from "shared/utils";
import moment from "moment";
import { BankStatementTableRowData } from "../types";
import {
  BankStatementItem,
  BankStatementItemData
} from "views/applications-id-report/slices/CombinedIncomeReportSlice/types/verified/bank-statment";
import { DepositTabs } from "./DepositTabs";

export const ExpandedTableRow = ({
  rowData
}: {
  rowData: BankStatementTableRowData;
}) => {
  const [activeTab, setActiveTab] = useState<"CompanyDeposits" | "AllDeposits">(
    "CompanyDeposits"
  );

  const { docScore, reasonCodes } = useMemo(
    () => ({
      docScore: rowData.raw_data?.trust_score ?? 0,
      reasonCodes: getBankStatementReasonCodes(rowData)
    }),
    [rowData]
  );

  const alertScore = resolveAuthenticityScore(rowData) ?? docScore;
  const problems = resolveDocumentProblems(rowData);
  const cardProblems = problems.filter(
    (problem) => problem.label !== NAME_MISMATCH_LABEL
  );

  const {
    bank_accounts: bankAccounts,
    name,
    address
  } = rowData.raw_data?.parsed_details || {};

  const nameMismatch = rowData.additional_data?.name_mismatch;

  return (
    <>
      <div className="space-y-3 mb-4">
        {nameMismatch ? (
          <NameMismatchAlert nameMismatch={nameMismatch} />
        ) : null}

        <Show if={problems.length > 1}>
          {cardProblems.map((problem) => (
            <DocumentIssueAlert
              key={problem.label}
              label={problem.label}
              description={problem.description}
            />
          ))}
        </Show>

        <AuthenticityScoreAlert
          tone={resolveAuthenticityTone(resolveFraudRisk(rowData), alertScore)}
          score={alertScore}
          issues={toFraudRiskLines(
            reasonCodes,
            rowData.additional_data?.risk_assessment_summary ??
              resolveRiskSummary(rowData)
          )}
        />
      </div>

      <Show if={rowData.hasTransactions}>
        <BankAccountDetailsSlice
          bankAccount={bankAccounts?.[0]}
          holderName={name?.variants[0] || name?.raw}
          address={address}
        />
        <IncomeSummarySlice bankAccount={bankAccounts?.[0]} />

        <DepositTabs
          companyName={rowData.companyName}
          activeTab={activeTab}
          setActiveTab={setActiveTab}
        />
        <SimpleTable<BankStatementItem | BankStatementItemData>
          data={
            (activeTab === "CompanyDeposits"
              ? rowData.usedItems
              : rowData.items) || []
          }
          columns={[
            {
              label: "Name",
              render: (d) => String(d.data?.transaction.description) || "-",
              className: "w-2/3"
            },
            {
              label: "Date",
              render: (d) =>
                d.data?.transaction.date
                  ? moment(d.data.transaction.date).format("ll")
                  : "-"
            },
            {
              label: "Amount",
              render: (d) => {
                const formattedValue = d.data?.transaction.amount
                  ? formatToCurrency({
                      cents: d.data.transaction.amount,
                      currency: d.data.transaction.currency_code
                    })
                  : "-";
                const isPositive =
                  d.data?.transaction.amount && d.data.transaction.amount >= 0;

                return (
                  <p
                    className={cx(
                      isPositive ? "text-success-700" : "text-danger-700"
                    )}
                  >
                    {isPositive ? "+ " : "- "}
                    {formattedValue}
                  </p>
                );
              },
              className: "text-right"
            }
          ]}
        />
      </Show>

      <div className="h-px w-full bg-alpha-100" />
    </>
  );
};
